import { Box, IconButton, useTheme, InputBase, MenuItem, Select, Typography, Button, TextField, CircularProgress, FormControlLabel, Checkbox, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { ColorModeContext, tokens } from "../../theme";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import Tooltip from "@mui/material/Tooltip";
import { handleLogout } from "../../utils";
import { useNavigate } from "react-router-dom";
import { learncardsContext } from "../../App";
import { shuffle } from 'lodash';
import { API, Auth } from "aws-amplify";
import { keyframes } from '@mui/system';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'; // Import trophy icon
import AddIcon from '@mui/icons-material/Add';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';

const Topbar = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const colorMode = useContext(ColorModeContext);
    const navigate = useNavigate();

    const [email, setEmail] = useState("unknown");
    const { setSelected, selectedOption, setSelectedOption, setLoading, setCardData, setOptions, options, userInfo, setUserInfo, exerciseChecked, setExerciseChecked } = useContext(learncardsContext);

    const [newSetName, setNewSetName] = useState("");
    const [showNewSetInput, setShowNewSetInput] = useState(false);
    const [isCreatingSet, setIsCreatingSet] = useState(false);

    const [loadingEmojis] = useState(['👋', '😊', '🎉', '🚀', '🌈']);
    const [currentEmojiIndex, setCurrentEmojiIndex] = useState(0);

    useEffect(() => {
        getOptions();
    }, []);

    useEffect(() => {
        getData();
    }, [selectedOption]);

    useEffect(() => {
        if (!userInfo) {
            const interval = setInterval(() => {
                setCurrentEmojiIndex((prevIndex) => (prevIndex + 1) % loadingEmojis.length);
            }, 500);
            return () => clearInterval(interval);
        }
    }, [userInfo, loadingEmojis]);

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
        if (event.target.value && !['/cards', '/'].includes(window.location.pathname)) {
            navigate('/'); // Navigate to root                
            setSelected('Oefenen');
        }
    };

    const handleExerciseToggle = (event, newAlignment) => {
        setExerciseChecked(newAlignment === 'Oefenen'); // Update state based on toggle
    };

    const getOptions = async () => {
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken().getJwtToken();
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const route = "/list";

            // Make the API request
            const response = await API.get("FRONTEND_HTTP_API", route, { headers });
            // Extract options from the response
            const fetchedOptions = response.data;
            //hide archived sets. Ie. sets starting with archived/
            //const fetchedOptions = response.data.filter(option => !option.name.startsWith('archived/'));


            // Check if email is part of the response
            setUserInfo(response.userInfo);
            if (response.userInfo && response.userInfo.email) {
                setEmail(response.userInfo.email);
            } else {
                console.warn("Email not found in response:", response);
            }

            // Find the option with the latest 'last_modified' date and not archived
            const latestOption = fetchedOptions.reduce((latest, current) => {
                if (current.name.startsWith('archived/')) {
                    return latest;
                }
                return new Date(current.last_modified) > new Date(latest.last_modified) ? current : latest;
            }, fetchedOptions[0]);

            // Set options and selected option
            setOptions(fetchedOptions);
            setSelectedOption(latestOption.name); // Set the option with the latest date            
        } catch (error) {
            console.error("Error fetching options:", error);
        } finally {
            setLoading(false);
        }
    };

    const getData = async () => {
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken().getJwtToken();
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const route = "/data?selectie=" + selectedOption;

            // Make the API request
            if (selectedOption) {
                let response = await API.get("FRONTEND_HTTP_API", route, {
                    headers: headers,
                });
                // Shuffle the array of cards
                const shuffledData = shuffle(response.data.cards);
                // Set the shuffled cards
                setCardData({ ...response, data: shuffledData });
            }
        } catch (error) {
            console.error("Error updating settings:", error);
        } finally {
            setLoading(false);
        }
    };

    // Define the recording pulse animation
    const recordingPulse = keyframes`
        0% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.2);
            opacity: 0.7;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    `;

    // Define a bouncing animation for the trophy
    const bounce = keyframes`
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
    `;

    const isCoach = (userInfo && userInfo.role === 'L2');
    const isRecording = isCoach && !exerciseChecked;

    const handleCreateSetClick = async () => {
        if (!newSetName.trim()) return; // Prevent creating empty sets
        setIsCreatingSet(true);
        try {
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken().getJwtToken();
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            };
            const response = await API.post("FRONTEND_HTTP_API", "/createSet", {
                body: { name: newSetName },
                headers,
            });
            console.log("Create set successful:", response);

            // Update options with the new set
            const newSet = { name: newSetName, last_modified: new Date().toLocaleString() };
            setOptions([...options, newSet]);
            setSelectedOption(newSetName);
            setNewSetName(""); // Clear the input field after creating a set
            setShowNewSetInput(false); // Hide the input field and button
            navigate('/cards');
        } catch (error) {
            console.error("Error creating set:", error);
            //set already exists
            setNewSetName("");
            //alert
            alert("Bestaande verzameling");
        } finally {
            setIsCreatingSet(false);
        }
    };

    return (
        <Box display="flex" justifyContent="space-between" p={2} id="topbar" sx={{
            backgroundColor: colors.primary[400], // Subtle background color
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', // Subtle shadow
            position: 'sticky',
            top: 0,
            zIndex: 1000,
        }}>
            <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>
                    {userInfo ? 'Kies een les uit de lijst:' : `${loadingEmojis[currentEmojiIndex]} `}
                </Typography>

                {userInfo ? (
                    <>
                        <Select
                            value={selectedOption}
                            onChange={handleChange}
                            style={{ minWidth: 120, marginRight: 10 }}
                        >
                            {options && options.map((option) => {
                                if (option.name.startsWith('archived/')) {
                                    return null;
                                }
                                return (
                                    <MenuItem key={option.name} value={option.name}>
                                        {option.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>

                        <IconButton title="Voeg een nieuwe verzameling toe" onClick={() => setShowNewSetInput(!showNewSetInput)}>
                            <LibraryAddIcon />
                        </IconButton>

                        {showNewSetInput && (
                            <Box display="flex" alignItems="center">
                                <TextField
                                    label="Naam nieuwe verzameling"
                                    value={newSetName}
                                    onChange={(e) => setNewSetName(e.target.value)}
                                    size="small"
                                    sx={{ width: '300px', mr: 2 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleCreateSetClick}
                                    size="small"
                                    disabled={isCreatingSet || !newSetName.trim()}
                                >
                                    {isCreatingSet ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        "Maak NIEUWE verzameling"
                                    )}
                                </Button>
                            </Box>
                        )}
                    </>
                ) : (
                    <Typography variant="h6" sx={{ animation: `${bounce} 1s ease-in-out infinite` }}>
                        {loadingEmojis[currentEmojiIndex]}
                    </Typography>
                )}
            </Box>

            {/* ICONS */}
            <Box>
                <ToggleButtonGroup
                    value={exerciseChecked ? 'Oefenen' : 'Tracken'}
                    exclusive
                    onChange={handleExerciseToggle}
                    aria-label="exercise toggle"
                    title="In Oefen modus, worden de kleuren NIET bewaard voor de volgende keer."
                >
                    <ToggleButton value="Oefenen" aria-label="Oefenen">
                        Oefenen
                    </ToggleButton>
                    <ToggleButton value="Tracken" aria-label="Tracken"
                        title="In Track modus, worden de kleuren bewaard voor de volgende keer.">
                        Tracken
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Box display="flex">
                {/* Greeting Message with Recording Symbol or Trophy */}
                <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {userInfo ? (
                            isRecording ? (
                                <>
                                    <Box
                                        sx={{

                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: 'red',
                                            marginRight: 1,
                                            animation: `${recordingPulse} 2s ease-in-out infinite`,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{

                                            color: 'red',
                                            fontWeight: 'bold',
                                            marginRight: 1
                                        }}
                                    >
                                        REC
                                    </Typography>
                                </>
                            ) : (
                                <EmojiEventsIcon
                                    sx={{
                                        display: 'none',
                                        color: 'gold',
                                        marginRight: 1,
                                        // animation: `${bounce} 1s ease-in-out infinite`,
                                    }}
                                />
                            )
                        ) : (
                            <Typography variant="h6" sx={{ animation: `${bounce} 1s ease-in-out infinite` }}>
                                {loadingEmojis[currentEmojiIndex]}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="h6">
                        {userInfo ? `👋 ${email}` : loadingEmojis[currentEmojiIndex]}
                    </Typography>
                </Box>

                <Tooltip title={userInfo ? `Uitloggen (${email})` : ''} disabled={!userInfo}>
                    <IconButton onClick={handleLogout} disabled={!userInfo}>
                        <PersonOutlinedIcon />
                    </IconButton>
                </Tooltip>

                <IconButton></IconButton>
            </Box>
        </Box>
    );
};

export default Topbar;